import { Link, useNavigate } from "@tanstack/react-router";
import type { Perfil } from "@vetbolso/contracts";
import {
  type Acerto,
  buscar,
  checarReceita,
  dataBR,
  espMatch,
  faixa,
  fmt,
  limiaresDa,
  MASCARA_CPF,
  MASCARA_DECIMAL,
  type Mascara,
  nomeEspecie,
  PERFIS_INFO,
  pequeno,
  resultadoDose,
} from "@vetbolso/core";
import { Icone, mascarar, type NomeIcone } from "@vetbolso/ui";
import { type ChangeEvent, useState } from "react";
import {
  Busca,
  Cabeca,
  CampoPeso,
  Cartao,
  combina,
  Destaque,
  Grupo,
  MOSTRAR_FONTES,
  Pares,
  Refs,
  SeletorUnidade,
  SemResultado,
  type Tom,
  textoPeso,
  Vazio,
} from "../comp.tsx";
import { useApp, useBase } from "../estado.tsx";
import { TOXCATS } from "../folhas.tsx";

type Destino = { to: string; params?: Record<string, string> } | { toxcat: string };
const MODS: Record<string, { t: string; s: string; i: NomeIcone; tom: Tom; d: Destino }> = {
  dose: { t: "Calcular dose", s: "Por peso, com a via e o intervalo", i: "syringe", tom: "dose", d: { to: "/doses" } },
  bula: {
    t: "Bulário",
    s: "Indicações, interações e efeitos",
    i: "livroAberto",
    tom: "apres",
    d: { to: "/m/$sub", params: { sub: "bulario" } },
  },
  tox: { t: "Comeu o quê?", s: "Limiar por mg/kg e conduta", i: "alert", tom: "tox", d: { to: "/toxicos" } },
  humano: { t: "Remédio humano no pet", s: "Paracetamol, ibuprofeno, xilitol", i: "pill", tom: "tox", d: { toxcat: "humano" } },
  calc: {
    t: "Calculadoras",
    s: "Fluidos, energia, transfusão, BSA",
    i: "calc",
    tom: "inter",
    d: { to: "/m/$sub", params: { sub: "calculadoras" } },
  },
  vac: { t: "Vacinas", s: "Protocolos e obrigatórias", i: "shield", tom: "ind", d: { to: "/m/$sub", params: { sub: "vacinas" } } },
  mdr1: { t: "MDR1", s: "Fármacos de risco por raça", i: "dna", tom: "efeitos", d: { to: "/m/$sub", params: { sub: "mdr1" } } },
  rx: { t: "Montar receita", s: "Campos exigidos pelo CFMV", i: "file", tom: "mec", d: { to: "/receita" } },
  farm: {
    t: "Posso aviar?",
    s: "Receita veterinária na farmácia",
    i: "store",
    tom: "inter",
    d: { to: "/m/$sub", params: { sub: "farmacia" } },
  },
  agro: { t: "Exige receita?", s: "O que o balcão pode vender", i: "store", tom: "campo", d: { to: "/m/$sub", params: { sub: "agro" } } },
  carencia: { t: "Carência", s: "Por produto registrado", i: "clock", tom: "campo", d: { to: "/m/$sub", params: { sub: "carencia" } } },
  prog: {
    t: "Programas sanitários",
    s: "Brucelose, raiva, AIE, GTA",
    i: "route",
    tom: "campo",
    d: { to: "/m/$sub", params: { sub: "programas" } },
  },
  plantas: {
    t: "Plantas tóxicas",
    s: "As que mais matam no pasto",
    i: "leaf",
    tom: "ind",
    d: { to: "/m/$sub", params: { sub: "plantas" } },
  },
  ref: { t: "Sinais vitais", s: "Temperatura, FC e FR", i: "thermo", tom: "efeitos", d: { to: "/m/$sub", params: { sub: "referencia" } } },
  quiz: { t: "Questão do dia", s: "Teste o que você sabe", i: "cap", tom: "apres", d: { to: "/m/$sub", params: { sub: "estudo" } } },
  mapas: { t: "Mapas mentais", s: "Estude por ramos", i: "rede", tom: "apres", d: { to: "/m/$sub", params: { sub: "mapas" } } },
};

function useIr() {
  const navegar = useNavigate();
  const { setToxCat } = useApp();
  return (d: Destino) => {
    if ("toxcat" in d) {
      setToxCat(d.toxcat);
      navegar({ to: "/toxicos" });
    } else navegar({ to: d.to, params: d.params } as never);
  };
}

function CartaoModulo({ k, largo }: { k: string; largo?: boolean }) {
  const m = MODS[k]!;
  const ir = useIr();
  return (
    <button type="button" className={`mod t-${m.tom}${largo ? " wide" : ""}`} onClick={() => ir(m.d)}>
      <span className="mi" aria-hidden="true">
        <Icone nome={m.i} />
      </span>
      <span className="mt">
        <b>{m.t}</b>
        <span>{m.s}</span>
      </span>
      {largo ? <Icone nome="right" className="sm item-seta" /> : null}
    </button>
  );
}

const POR_PERFIL = (peq: boolean): Record<Perfil, string[]> => ({
  vet: peq ? ["dose", "bula", "tox", "calc", "vac", "mdr1", "rx"] : ["carencia", "prog", "plantas", "vac", "dose", "rx"],
  farmacia: ["farm", "humano", "bula", "dose", "tox", "rx"],
  agro: ["agro", "carencia", "prog", "vac", "plantas", "tox"],
  estudante: ["quiz", "mapas", "bula", "calc", "dose", "tox", peq ? "vac" : "plantas"],
});
const OLA = (peq: boolean): Record<Perfil, string> => ({
  vet: peq ? "Plantão de pequenos" : "Atendimento de campo",
  farmacia: "Receita veterinária no balcão",
  agro: "Balcão agropecuário",
  estudante: "Estudo",
});

const VISUAL_BUSCA: Record<Acerto["tipo"], [NomeIcone, Tom]> = {
  bula: ["livroAberto", "apres"],
  dose: ["syringe", "dose"],
  tox: ["alert", "tox"],
  prod: ["caixa", "campo"],
  regra: ["file", "inter"],
};

export function TelaInicio() {
  const base = useBase();
  const { especie: e, perfil, q, setQ, abrir, peso, unPeso } = useApp();
  const navegar = useNavigate();
  const peq = pequeno(e);
  const mods = POR_PERFIL(peq)[perfil];
  const hits = buscar(base, q, e);
  return (
    <>
      <div className="hello">
        <span className="eyebrow">
          {PERFIS_INFO.find((p) => p.id === perfil)?.nome} · {nomeEspecie(e)}
        </span>
        <h2>{OLA(peq)[perfil]}</h2>
        <p className="sub">Resolva a dúvida na hora, com a dose pelo peso.</p>
      </div>
      <label className="search">
        <Icone nome="search" />
        <input
          value={q}
          onChange={(ev) => setQ(ev.target.value)}
          placeholder="Buscar fármaco, tóxico ou regra"
          autoComplete="off"
          aria-label="Buscar"
        />
      </label>
      {q.trim().length >= 2 ? (
        hits.length ? (
          <Grupo titulo="Resultados" n={hits.length} densos>
            {hits.map((h) => (
              <Cartao
                key={h.tipo + h.id}
                icone={VISUAL_BUSCA[h.tipo][0]}
                tom={VISUAL_BUSCA[h.tipo][1]}
                titulo={h.t}
                sub={h.s}
                aoTocar={() => abrir({ t: h.tipo, id: h.id })}
              />
            ))}
          </Grupo>
        ) : (
          <Vazio icone="search">
            Nada para “{q}” em {nomeEspecie(e).toLowerCase()}.
          </Vazio>
        )
      ) : null}
      {peq && (perfil === "vet" || perfil === "estudante") ? (
        <button type="button" className="sos" onClick={() => navegar({ to: "/m/$sub", params: { sub: "emergencia" } })}>
          <span className="sos-ic" aria-hidden="true">
            <Icone nome="pulse" />
          </span>
          <span className="sos-t">
            <b>Emergência · RCP</b>
            <span>Carrinho de parada para {textoPeso(peso, unPeso)}</span>
          </span>
          <Icone nome="right" className="sm" />
        </button>
      ) : null}
      <section className="grupo" aria-label="Atalhos">
        <div className="grupo-h">
          <span className="group-label">Atalhos</span>
        </div>
        <div className="mods">
          <CartaoModulo k={mods[0]!} largo />
          {mods.slice(1).map((k) => (
            <CartaoModulo key={k} k={k} />
          ))}
        </div>
      </section>
      {MOSTRAR_FONTES ? (
        <Link to="/m/$sub" params={{ sub: "fontes" }} className="basecard t-ind">
          <span className="basecard-ic" aria-hidden="true">
            <Icone nome="shield" />
          </span>
          <span className="basecard-t">
            <b>{base.fontes.length} fontes primárias</b>
            <span>Base conferida em {dataBR(base.meta?.conferidoEm)} · ver como auditamos</span>
          </span>
          <Icone nome="right" className="sm item-seta" />
        </Link>
      ) : null}
    </>
  );
}

export function TelaDoses() {
  const base = useBase();
  const { especie: e, peso, unPeso, abrir } = useApp();
  const [q, setQ] = useState("");
  const doses = base.doses.filter((d) => d.especie === e && combina(q, d.farmaco, d.classe, d.via));
  const prods = base.produtos.filter((p) => p.especies.includes(e as never) && combina(q, p.produto, p.farmaco, p.fabricante));
  const grupos = new Map<string, typeof doses>();
  for (const d of doses) grupos.set(d.classe || "Outros", [...(grupos.get(d.classe || "Outros") ?? []), d]);
  return (
    <>
      <Cabeca titulo="Doses" sub={`${nomeEspecie(e)} · peso de referência ${textoPeso(peso, unPeso)}`} voltar={false} />
      <CampoPeso id="peso-doses" />
      <Busca valor={q} aoMudar={setQ} placeholder="Fármaco, classe ou produto" rotulo="Buscar dose" />
      {[...grupos].map(([g, ds]) => (
        <Grupo key={g} titulo={g} n={ds.length}>
          {ds.map((d) => {
            const r = d.semDose ? null : resultadoDose(d, peso);
            const temBula = !!d.bula && base.bulario.some((b) => b.id === d.bula);
            return (
              <Cartao
                key={d.id}
                icone="syringe"
                tom="dose"
                titulo={d.farmaco}
                sub={[d.via, d.intervalo].filter(Boolean).join(" · ") || d.contexto}
                valor={
                  d.semDose ? (
                    "sem dose"
                  ) : (
                    <>
                      <b>{faixa(d.min ?? 0, d.max, 3)}</b>
                      {d.un}
                    </>
                  )
                }
                aoTocar={() => abrir(temBula ? { t: "bula", id: d.bula as string } : { t: "dose", id: d.id })}
              >
                {r?.tipo === "peso" ? (
                  <Destaque
                    icone="calc"
                    v={
                      <>
                        <b>{faixa(r.calc.lo, r.calc.hi, 3)}</b> {r.calc.base}
                      </>
                    }
                    q={`${r.quando} para ${textoPeso(peso, unPeso)}`}
                  />
                ) : r?.tipo === "fixa" ? (
                  <Destaque
                    icone="calc"
                    v={
                      <>
                        <b>{faixa(r.lo, r.hi, 3)}</b> {r.un}
                      </>
                    }
                    q="dose total por animal, independe do peso"
                  />
                ) : null}
                {d.controle || temBula ? (
                  <span className="item-chips">
                    {d.controle ? <span className="etq atencao">{d.controle}</span> : null}
                    {temBula ? <span className="etq">bulário completo</span> : null}
                  </span>
                ) : null}
              </Cartao>
            );
          })}
        </Grupo>
      ))}
      {prods.length ? (
        <Grupo titulo="Produtos registrados no MAPA" n={prods.length}>
          {prods.map((p) => (
            <Cartao
              key={p.id}
              icone="caixa"
              tom="campo"
              titulo={p.produto}
              sub={[p.farmaco, p.concentracao].filter(Boolean).join(" · ")}
              aoTocar={() => abrir({ t: "prod", id: p.id })}
            >
              <Pares
                itens={[
                  ["Carência carne", p.carenciaCarne ?? "—"],
                  ["Carência leite", p.carenciaLeite ?? "—"],
                ]}
              />
            </Cartao>
          ))}
        </Grupo>
      ) : null}
      {!doses.length && !prods.length && q ? <SemResultado termo={q} /> : null}
      {!doses.length && !prods.length && !q ? <Vazio>Nenhuma dose de {nomeEspecie(e).toLowerCase()} nesta versão.</Vazio> : null}
    </>
  );
}

export function TelaToxicos() {
  const base = useBase();
  const { especie: e, toxCat, setToxCat, abrir } = useApp();
  const navegar = useNavigate();
  const [q, setQ] = useState("");
  const tox = base.toxicos.filter(
    (t) => espMatch(t, e) && (toxCat === "todos" || t.cat === toxCat) && combina(q, t.agente, t.sinonimos, t.resumo),
  );
  const cats = TOXCATS.filter(([id]) => id === "todos" || base.toxicos.some((t) => t.cat === id && espMatch(t, e)));
  const plantas = base.plantas.filter((p) => espMatch(p, e));
  return (
    <>
      <Cabeca titulo="Comeu o quê?" sub={`${nomeEspecie(e)} · limiares por peso`} voltar={false} />
      <Busca valor={q} aoMudar={setQ} placeholder="Alimento, remédio, planta ou produto" rotulo="Buscar tóxico" />
      {/* biome-ignore lint/a11y/useSemanticElements: grupo de botões de alternância, como no protótipo */}
      <div className="chips" role="group" aria-label="Categoria">
        {cats.map(([id, n]) => (
          <button key={id} type="button" className="chip" aria-pressed={toxCat === id} onClick={() => setToxCat(id)}>
            {n}
          </button>
        ))}
      </div>
      {tox.length ? (
        <ul className="cartoes">
          {tox.map((t) => {
            const lim = limiaresDa(t, e);
            // Menor limiar conferido, na unidade do primeiro (as fichas não misturam unidades por espécie).
            const com = lim.filter((l) => l.valor != null);
            const menor = com.filter((l) => l.un === com[0]?.un).sort((x, y) => (x.valor ?? 0) - (y.valor ?? 0))[0];
            return (
              <Cartao key={t.id} icone="alert" tom="tox" titulo={t.agente} sub={t.resumo} aoTocar={() => abrir({ t: "tox", id: t.id })}>
                <span className="item-chips">
                  <span className="etq">{TOXCATS.find((x) => x[0] === t.cat)?.[1]}</span>
                  {menor ? (
                    <span className="etq mal">
                      a partir de {fmt(menor.valor as number, 2)} {menor.un ?? ""}
                    </span>
                  ) : (
                    <span className="etq atencao">sem limiar para {nomeEspecie(e).toLowerCase()}</span>
                  )}
                  {t.calc && lim.length ? <span className="etq bom">calcula pelo peso</span> : null}
                </span>
              </Cartao>
            );
          })}
        </ul>
      ) : q ? (
        <SemResultado termo={q} />
      ) : (
        <Vazio>Nenhum tóxico conferido para {nomeEspecie(e).toLowerCase()} nesta categoria.</Vazio>
      )}
      {!pequeno(e) && plantas.length && (toxCat === "todos" || toxCat === "planta") ? (
        <ul className="cartoes">
          <Cartao
            icone="leaf"
            tom="campo"
            titulo="Plantas tóxicas do pasto"
            sub={plantas.map((p) => p.nome.split(" (")[0]).join(", ")}
            aoTocar={() => navegar({ to: "/m/$sub", params: { sub: "plantas" } })}
          />
        </ul>
      ) : null}
      <Refs refs={(base.meta as { toxRefs?: import("@vetbolso/contracts").Ref[] } | undefined)?.toxRefs} />
    </>
  );
}

export function TelaReceita() {
  const base = useBase();
  const { especie: e, rx, setRx, abrir, unPeso } = useApp();
  const regraCtrl = base.regras.find((r) => r.id === "rx-controlados");
  const controlados = rx.itens.filter((i) => i.controle);
  const campos = base.receita?.campos ?? [];
  const chk = checarReceita(campos, rx);
  const set = (k: "animal" | "tutor" | "cpf" | "peso", m?: Mascara) => (ev: ChangeEvent<HTMLInputElement>) => {
    const v = m ? mascarar(ev, m) : ev.target.value;
    setRx((r) => ({ ...r, [k]: v }));
  };
  const normas = base.regras.filter((r) => r.publico === "vet" || r.id === "rx-controlados");
  return (
    <>
      <Cabeca titulo="Receita" sub={`${nomeEspecie(e)} · prévia, sem emissão`} voltar={false} />
      <div className="card">
        <h3>Paciente e tutor</h3>
        <div className="fields">
          <div className="field">
            <label htmlFor="rx-animal">Nome do animal</label>
            <input id="rx-animal" value={rx.animal} onChange={set("animal")} placeholder="Ex.: Thor" />
          </div>
          <div className="field">
            <label htmlFor="rx-peso">Peso</label>
            <div className="caixa-un">
              <input
                id="rx-peso"
                className="num"
                inputMode="decimal"
                value={rx.peso}
                onChange={set("peso", MASCARA_DECIMAL)}
                placeholder="0,0"
              />
              <SeletorUnidade
                rotulo="Unidade do peso"
                valor={rx.pesoUn ?? unPeso}
                opcoes={[
                  { v: "kg", rotulo: "kg" },
                  { v: "g", rotulo: "g" },
                ]}
                aoMudar={(u) => setRx((r) => ({ ...r, pesoUn: u }))}
              />
            </div>
          </div>
        </div>
        <div className="fields">
          <div className="field">
            <label htmlFor="rx-tutor">Tutor ou responsável</label>
            <input id="rx-tutor" value={rx.tutor} onChange={set("tutor")} placeholder="Nome completo" />
          </div>
          <div className="field">
            <label htmlFor="rx-cpf">CPF do tutor</label>
            <input
              id="rx-cpf"
              className="num"
              inputMode="numeric"
              value={rx.cpf}
              onChange={set("cpf", MASCARA_CPF)}
              placeholder="000.000.000-00"
            />
          </div>
        </div>
        <p className="sub">Os dados do paciente ficam só neste aparelho e somem ao fechar o app.</p>
      </div>
      <div className="card">
        <h3>Itens</h3>
        {rx.itens.length ? (
          rx.itens.map((it, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: o mesmo fármaco pode entrar duas vezes
            <div key={i} className="field">
              <label htmlFor={`rx-it-${i}`}>
                {it.farmaco}
                {it.controle ? <span style={{ color: "var(--warning)" }}> · {it.controle}</span> : null}
              </label>
              <textarea
                id={`rx-it-${i}`}
                rows={2}
                value={it.pos}
                onChange={(ev) => setRx((r) => ({ ...r, itens: r.itens.map((x, j) => (j === i ? { ...x, pos: ev.target.value } : x)) }))}
              />
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                <Refs refs={it.refs} />
                <button
                  type="button"
                  className="btn ghost sm"
                  onClick={() => setRx((r) => ({ ...r, itens: r.itens.filter((_, j) => j !== i) }))}
                >
                  Remover
                </button>
              </div>
            </div>
          ))
        ) : (
          <Vazio icone="syringe">
            Abra uma dose e toque em “Adicionar à receita”.
            <Link to="/doses" className="btn sm">
              Ir para Doses
            </Link>
          </Vazio>
        )}
      </div>
      {controlados.length && regraCtrl ? (
        <div className="alert warning">
          <Icone nome="alert" className="sm" />
          <div>
            <p>
              <b>{regraCtrl.titulo}.</b> {regraCtrl.resumo}
            </p>
            <Refs refs={regraCtrl.refs} />
          </div>
        </div>
      ) : null}
      {campos.length ? (
        <div className="card">
          <h3>
            Campos exigidos · {chk.preenchidos}/{chk.total}
          </h3>
          {chk.itens.map((c) => (
            <div key={c.t} className={`check${c.ok ? " ok" : ""}`}>
              <span className="box">{c.ok ? <Icone nome="check" /> : null}</span>
              <span>{c.t}</span>
            </div>
          ))}
          <Refs refs={base.receita?.refs} />
        </div>
      ) : null}
      <section className="paper" aria-label="Prévia da receita">
        <h4>Receita veterinária</h4>
        <div className="line">
          <div>
            <span>Paciente</span> {rx.animal || "—"}
          </div>
          <div>
            <span>Espécie</span> {nomeEspecie(e)}
          </div>
          <div>
            <span>Peso</span> {rx.peso ? `${rx.peso} ${rx.pesoUn ?? unPeso}` : "—"}
          </div>
        </div>
        <div className="line">
          <div>
            <span>Tutor</span> {rx.tutor || "—"}
          </div>
          <div>
            <span>CPF</span> {rx.cpf || "—"}
          </div>
        </div>
        {rx.itens.length ? (
          <ol>
            {rx.itens.map((i, n) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: prévia só de leitura
              <li key={n}>
                <b>{i.farmaco}</b>
                <br />
                {i.pos}
              </li>
            ))}
          </ol>
        ) : (
          <p className="sub">Sem itens.</p>
        )}
        <div className="sign">Local e data · assinatura e carimbo com nome e CRMV</div>
      </section>
      {rx.itens.length ? (
        <button type="button" className="btn ghost" onClick={() => window.print()}>
          <Icone nome="file" />
          Imprimir prévia
        </button>
      ) : null}
      {normas.length ? (
        <>
          <div className="group-label">Normas da receita</div>
          <div className="list">
            {normas.map((r) => (
              <button key={r.id} type="button" className="row" onClick={() => abrir({ t: "regra", id: r.id })}>
                <span className="tile">
                  <Icone nome="library" className="sm" />
                </span>
                <span className="t">
                  <b>{r.titulo}</b>
                </span>
                <Icone nome="right" className="sm" />
              </button>
            ))}
          </div>
        </>
      ) : null}
    </>
  );
}

const GRUPOS_MAIS: [string, [string, string, NomeIcone][]][] = [
  [
    "Clínica",
    [
      ["bulario", "Bulário", "livroAberto"],
      ["emergencia", "Emergência · RCP", "pulse"],
      ["calculadoras", "Calculadoras", "calc"],
      ["dor", "Escalas de dor", "pulse"],
      ["vacinas", "Vacinas", "shield"],
      ["mdr1", "MDR1 e raças", "dna"],
      ["referencia", "Sinais vitais", "thermo"],
    ],
  ],
  [
    "Balcão",
    [
      ["farmacia", "Farmácia: posso aviar?", "store"],
      ["agro", "Agropecuária: exige receita?", "store"],
    ],
  ],
  [
    "Campo",
    [
      ["carencia", "Carência por produto", "clock"],
      ["programas", "Programas sanitários", "route"],
      ["plantas", "Plantas tóxicas", "leaf"],
    ],
  ],
  [
    "Estudo",
    [
      ["estudo", "Questões", "cap"],
      ["mapas", "Mapas mentais", "rede"],
    ],
  ],
  [
    "Sobre a base",
    [
      ...(MOSTRAR_FONTES ? [["fontes", "Fontes e auditoria", "library"] as [string, string, NomeIcone]] : []),
      ["conta", "Conta e sincronização", "info"],
    ],
  ],
];

export function TelaMais() {
  const { perfil, setPerfil } = useApp();
  return (
    <>
      <Cabeca titulo="Mais" sub="Todos os módulos" voltar={false} />
      {GRUPOS_MAIS.map(([g, itens]) => (
        <div key={g} style={{ display: "contents" }}>
          <div className="group-label">{g}</div>
          <div className="list">
            {itens.map(([id, n, i]) => (
              <Link key={id} to="/m/$sub" params={{ sub: id }} className="row">
                <span className="tile">
                  <Icone nome={i} className="sm" />
                </span>
                <span className="t">
                  <b>{n}</b>
                </span>
                <Icone nome="right" className="sm" />
              </Link>
            ))}
          </div>
        </div>
      ))}
      <div className="group-label">Perfil</div>
      <div className="chips">
        {PERFIS_INFO.map((p) => (
          <button key={p.id} type="button" className="chip" aria-pressed={perfil === p.id} onClick={() => setPerfil(p.id)}>
            {p.nome}
          </button>
        ))}
      </div>
    </>
  );
}
